import { useInfiniteQuery } from '@tanstack/react-query';

import {
  AdminChatConversationDetails,
  getChatConversations,
  getChatMessageCounts,
} from '~/services/TripPlannerService';
import UsersService from '~/services/UsersService';

export const CHAT_REVIEW_BATCH_SIZE = 20;
export type ChatAccountFilter = 'all' | 'registered' | 'guest';
export interface ChatReviewRow extends AdminChatConversationDetails {
  userMessages: number;
  totalMessages: number;
  registeredUser: boolean;
}
export interface ChatReviewFilters {
  chatId?: string;
  userId?: string;
  account: ChatAccountFilter;
  minMessages: number;
  brand: string;
}

export async function fetchChatReviewBatch(filters: ChatReviewFilters, page: number, signal: AbortSignal) {
  const response = await getChatConversations(
    { page, pageSize: CHAT_REVIEW_BATCH_SIZE, chatId: filters.chatId, userId: filters.userId },
    { signal },
  );
  const chats = response.result.trips;
  const ids = Array.from(new Set(chats.map((chat) => chat.createdBy).filter(Boolean)));
  const users = ids.length ? await UsersService.getUsersSummaryByIds(ids, filters.brand) : new Map();
  const candidates = chats.filter(
    (chat) => filters.account === 'all' || (filters.account === 'registered') === users.has(chat.createdBy),
  );
  const rows: ChatReviewRow[] = [];
  // The API has no aggregate count field; keep transcript reads bounded and cache only counts.
  for (let start = 0; start < candidates.length; start += 4) {
    if (signal.aborted) throw new Error('Chat review search cancelled');
    const batch = await Promise.all(
      candidates.slice(start, start + 4).map(async (chat) => {
        const counts = await getChatMessageCounts(chat.id, { signal });
        return {
          ...chat,
          userMessages: counts.user,
          totalMessages: counts.total,
          registeredUser: users.has(chat.createdBy),
        };
      }),
    );
    rows.push(...batch.filter((chat) => chat.userMessages >= filters.minMessages));
  }
  return {
    rows,
    scanned: chats.length,
    total: response.result.numberOfTrips,
    nextPage: chats.length && page * CHAT_REVIEW_BATCH_SIZE < response.result.numberOfTrips ? page + 1 : undefined,
  };
}

export function useChatReviewList(filters: ChatReviewFilters) {
  return useInfiniteQuery({
    queryKey: ['trip-planner', 'review-list', filters],
    queryFn: ({ signal, pageParam }) => fetchChatReviewBatch(filters, pageParam, signal),
    initialPageParam: 1,
    getNextPageParam: (page) => page.nextPage,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
